import { TELEMEDICINE_CATEGORY_IDS } from '@/constants/category';
import { EMedicalCategoryType } from '@/entities/medical-category';

export interface SeoCategory {
  id: string;
  name: string;
  getSeoTitle: () => string;
  getSeoDescription: () => string;
  getKeywords: () => string;
}

export class DepartmentCategoryModel {
  constructor(
    public id: string,
    public name: string,
    public description: string,
    public icon: string
  ) {}

  getSeoTitle(): string {
    switch (this.id) {
      case TELEMEDICINE_CATEGORY_IDS.DEPARTMENT.PEDIATRICS:
        return '소아과(소아청소년과) 비대면진료 예약·약 처방 받기 - 닥터나우';
      default:
        return `${this.name} 비대면 진료 | 진료과·질환별 원격진료 - 닥터나우`;
    }
  }

  getSeoDescription(): string {
    switch (this.id) {
      case TELEMEDICINE_CATEGORY_IDS.DEPARTMENT.PEDIATRICS:
        return '아이 아플 때, 닥터나우에서 소아과 선생님께 비대면진료와 약 처방까지 빠르게 받아보세요. 아기 해열제까지 모두 비대면 처방 가능해요.';
      default:
        return `${this.name}에 맞는 의사를 빠르게 찾고, 병원 방문 없이 비대면진료로 약 처방까지 받아보세요.`;
    }
  }

  getKeywords(): string {
    switch (this.id) {
      case TELEMEDICINE_CATEGORY_IDS.DEPARTMENT.PEDIATRICS:
        return '소아과, 소아청소년과, 비대면진료, 일요일소아과, 비대면진료어플, 닥터나우';
      default:
        return '비대면진료, 원격진료, 온라인진료, 비대면처방, 약처방, 약배송, 약배달, 진료비';
    }
  }
}

export class PopularDepartmentCategoryModel {
  constructor(
    public id: string,
    public name: string
  ) {}
}

export class PopularTreatmentCategoryModel {
  constructor(
    public id: string,
    public name: string
  ) {}
}

export class TreatmentCategoryModel {
  constructor(
    public id: string,
    public name: string,
    public icon: string,
    private _treatments: {
      id: string;
      name: string;
    }[]
  ) {}

  private getSeoTitle(treatmentId: string): string {
    switch (treatmentId) {
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.COLD:
        return '감기·독감 비대면진료 예약하고 약 처방받기 - 닥터나우';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.ARTIFICIAL_TEARS:
        return '인공눈물, 비대면진료 예약하고 처방받기 - 닥터나우';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.HAIRLOSS:
        return '탈모 비대면진료 예약하고 탈모약 처방받기 - 닥터나우';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.ACNE:
        return '여드름 비대면진료 예약하고 여드름약 처방받기 - 닥터나우';
      default:
        return `${this.name} 비대면 진료 | 진료과·질환별 원격진료 - 닥터나우`;
    }
  }

  private getSeoDescription(treatmentId: string): string {
    switch (treatmentId) {
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.COLD:
        return '코감기, 인후통, 몸살 증상이 있으신가요? 병원 갈 필요 없이, 닥터나우에서 의사에게 감기·독감 비대면 진료 후 약 처방받아 보세요.';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.ARTIFICIAL_TEARS:
        return '라식/라섹 후 인공눈물 필요하세요? 닥터나우를 이용하면 안과 갈 필요 없이 비대면진료로 빠르게 인공눈물을 처방받을 수 있어요.';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.HAIRLOSS:
        return '탈모약 처방 저렴한 곳 찾는다면? 병원 갈 필요 없이, 닥터나우에서 최저가 탈모약 처방을 비대면으로 안전하게 받아 보세요.';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.ACNE:
        return '여드름으로 고생하고 있다면? 피부과 방문 없이 닥터나우에서 항생제 및 외용제를 비대면 진료로 간편하게 처방받아 보세요.';
      default:
        return `${this.name}에 맞는 의사를 빠르게 찾고, 병원 방문 없이 비대면진료로 약 처방까지 받아보세요.`;
    }
  }

  private getKeywords(treatmentId: string): string {
    switch (treatmentId) {
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.COLD:
        return '감기, 독감, 코감기, 몸살, 감기비대면진료, 감기약처방, 비대면처방, 비대면진료어플, 닥터나우';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.ARTIFICIAL_TEARS:
        return '인공눈물, 안구건조증, 비대면진료, 인공눈물대량처방, 인공눈물처방최대, 인공눈물비대면진료';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.HAIRLOSS:
        return '탈모약, 피나스테리드, 두타스테리드, 탈모비대면처방, 탈모비대면진료, 비대면진료어플, 닥터나우';
      case TELEMEDICINE_CATEGORY_IDS.TREATMENT.ACNE:
        return '여드름약, 여드름처방, 여드름비대면진료, 이소티논, 크레오신티, 비대면진료어플, 닥터나우';
      default:
        return '비대면진료, 원격진료, 온라인진료, 비대면처방, 약처방, 약배송, 약배달, 진료비';
    }
  }

  get treatments(): {
    id: string;
    name: string;
    getSeoTitle: () => string;
    getSeoDescription: () => string;
    getKeywords: () => string;
  }[] {
    return this._treatments.map((treatment) => ({
      id: treatment.id,
      name: treatment.name,
      getSeoTitle: () => this.getSeoTitle(treatment.id),
      getSeoDescription: () => this.getSeoDescription(treatment.id),
      getKeywords: () => this.getKeywords(treatment.id),
    }));
  }
}

export class DiagnosisCategoryInfoModel {
  constructor(
    public popularDepartmentCategories: PopularDepartmentCategoryModel[],
    public popularTreatmentCategories: PopularTreatmentCategoryModel[],
    public departmentCategories: DepartmentCategoryModel[],
    public treatmentCategories: TreatmentCategoryModel[]
  ) {}

  findDepartmentCategoryByCategoryId(categoryId: string): DepartmentCategoryModel | null {
    return this.departmentCategories.find((category) => category.id === categoryId) || null;
  }

  private findTreatmentCategoryByTreatmentId(treatmentId: string): TreatmentCategoryModel | null {
    return (
      this.treatmentCategories.find((category) =>
        category.treatments.some((treatment) => treatment.id === treatmentId)
      ) || null
    );
  }

  findTreatmentByTreatmentId(treatmentId: string): SeoCategory | null {
    const foundCategory = this.findTreatmentCategoryByTreatmentId(treatmentId);

    if (!foundCategory) {
      return null;
    }

    return foundCategory.treatments.find((treatment) => treatment.id === treatmentId) || null;
  }

  getDepartmentCategories(): DepartmentCategoryModel[] {
    return this.departmentCategories;
  }

  getCategoryNameByIdAndType(categoryId?: string, categoryType?: EMedicalCategoryType): string {
    if (!categoryId || !categoryType) {
      return '증상/과목';
    }
    if (categoryType === EMedicalCategoryType.DEPARTMENT) {
      return this.findDepartmentCategoryByCategoryId(categoryId)?.name ?? '';
    } else if (categoryType === EMedicalCategoryType.TREATMENT) {
      return this.findTreatmentByTreatmentId(categoryId)?.name ?? '';
    }
    return '증상/과목';
  }
}
